import { Alert, CircularProgress } from "@mui/material";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { Period } from "@/shared/domain/metrics";
import { useAllMetricDefinitions } from "@/shared/domain/metrics/hooks";

import {
  DASHBOARD_PARAMS,
  mergeParams,
  readPeriod,
  readRange,
} from "./components/dashboardParams";
import { MetricChartCard } from "./components/MetricChartCard";
import { MetricsControls } from "./components/MetricsControls";
import { MetricsEmptyState } from "./components/MetricsEmptyState";
import {
  periodFitsRange,
  periodForRange,
  rangeForPeriod,
  TimeRange,
  timeRangeToNsBounds,
} from "./components/timeRange";
import { TrendTileRow } from "./components/TrendTileRow";
import styles from "./MetricsDashboard.module.css";

export interface MetricsDashboardProps {
  orgId: string | undefined;
  /** Restrict every query to one device. Top-level dashboard omits this. */
  deviceId?: string;
  /** Restrict every query to one session. Top-level dashboard omits this. */
  sessionId?: string;
}

/**
 * Org-, device-, or session-scoped metrics dashboard. The same component
 * powers (a) the top-level metrics route (no filter), (b) the device-detail
 * "Metrics" tab (deviceId set), and (c) any session-scoped view (sessionId
 * set).
 *
 * Layout, top to bottom: global controls (time range + period) → a row of KPI
 * trend tiles → a chart card with per-card pickers and click-to-drill-down.
 * When the org has no definitions the whole thing is replaced by an onboarding
 * empty state.
 *
 * This component never renders the metric-definitions *management* table — that
 * lives behind the admin-only "Metric Definitions" tab in `MetricsContent`.
 */
export function MetricsDashboard({
  orgId,
  deviceId,
  sessionId,
}: MetricsDashboardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const range = readRange(searchParams);
  const period = readPeriod(searchParams, range);

  const {
    data: definitions,
    isLoading,
    error,
  } = useAllMetricDefinitions(orgId);

  // Range and period auto-fit each other, but only when the current pair no
  // longer fits (one too small for the other) — a still-valid combination is
  // left as the user set it. All view state lives in the URL (replace, so
  // controls don't pollute history) so the dashboard restores itself on reload
  // or browser-back.
  const handleRangeChange = (next: TimeRange) => {
    const nextPeriod = periodFitsRange(next, period)
      ? period
      : periodForRange(next);
    setSearchParams(
      mergeParams(searchParams, {
        [DASHBOARD_PARAMS.range]: next,
        [DASHBOARD_PARAMS.period]: nextPeriod,
      }),
      { replace: true },
    );
  };

  const handlePeriodChange = (next: Period) => {
    const nextRange = periodFitsRange(range, next)
      ? range
      : rangeForPeriod(next);
    setSearchParams(
      mergeParams(searchParams, {
        [DASHBOARD_PARAMS.period]: next,
        [DASHBOARD_PARAMS.range]: nextRange,
      }),
      { replace: true },
    );
  };

  // Anchor the window to a stable `now` per range selection. Recomputing
  // `timeRangeToNsBounds` on every render would shift the ns bounds (and thus
  // every aggregate query key) each time the dashboard re-renders — e.g. when
  // opening the drill-down dialog — causing the backing tiles/chart to refetch.
  const bounds = useMemo(() => timeRangeToNsBounds(range), [range]);
  const deviceIds = deviceId ? [deviceId] : undefined;
  const sessionIds = sessionId ? [sessionId] : undefined;

  if (isLoading) {
    return (
      <div className={styles.state}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Failed to load metrics: {error.message}</Alert>
    );
  }

  if (!definitions || definitions.length === 0) {
    return <MetricsEmptyState />;
  }

  return (
    <div className={styles.page}>
      <MetricsControls
        range={range}
        period={period}
        onRangeChange={handleRangeChange}
        onPeriodChange={handlePeriodChange}
      />
      <TrendTileRow
        definitions={definitions}
        orgId={orgId}
        period={period}
        startTimeNs={bounds.start_time_ns}
        endTimeNs={bounds.end_time_ns}
        deviceIds={deviceIds}
        sessionIds={sessionIds}
      />
      <MetricChartCard
        definitions={definitions}
        orgId={orgId}
        period={period}
        startTimeNs={bounds.start_time_ns}
        endTimeNs={bounds.end_time_ns}
        deviceIds={deviceIds}
        sessionIds={sessionIds}
      />
    </div>
  );
}
